import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { activityFeedQueryKeys } from '@/lib/services/activity-feed.query'
import { customerQueryKeys } from '@/lib/services/customers/customers.query'
import type { CustomerIdInput } from '@/lib/services/customers/customers.types'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { organizationQueryKeys } from '@/lib/services/organizations/organizations.query'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import {
  createMessageCustomer,
  createMessage,
  getMessage,
  getMessages,
  linkMessageCustomer,
  markMessageRead,
  markMessageUnread,
  softDeleteMessage,
  updateMessage,
  type MessageIndexQueryKeyEntries,
  type MessageScope,
  type MessageShowKeyEntries,
} from './messages.api'
import type {
  CreateMessageCustomerInput,
  CreateMessageInput,
  Message,
  MessageIdInput,
  MessageIndexFilters,
  MessageIndexResponse,
  UpdateMessageInput,
} from './messages.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface MessageIndexScope extends OrganizationScope {
  filters?: MessageIndexFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type MessageIndexQueryKey = [AdminQueryRoot & MessageIndexQueryKeyEntries]
type MessageShowQueryKey = [AdminQueryRoot & MessageShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

/** Mutations that act on one message take its id with the request fields. */
interface MessageMutationValues {
  messageId: MessageIdInput
}

interface MessageReadStateScope {
  organizationId: number
  messageId: MessageIdInput
}

/** What an optimistic read-state change replaced, so a failed request can put it back. */
interface MessageReadStateSnapshot {
  showQueryKey: QueryKey
  activityFeedQueryKey: QueryKey
  organizationQueryKey: QueryKey
  previousShowMessage: Message | undefined
  previousActivityFeed: unknown
  previousOrganization: Organization | undefined
}

export const messageQueryKeys = {
  all: [{ entity: 'message' }] satisfies AdminQueryKey,
  index: ({
    organizationId,
    filters,
    page,
    pageSize,
  }: MessageIndexScope = {}): MessageIndexQueryKey =>
    buildScopedAdminQueryKey(messageQueryKeys.all[0], {
      organizationId,
      scope: 'index',
      filters,
      page,
      pageSize,
    }),
  show: ({ organizationId, messageId }: MessageScope): MessageShowQueryKey =>
    buildScopedAdminQueryKey(messageQueryKeys.all[0], {
      organizationId,
      scope: 'show',
      messageId: Number(messageId),
    }),
}

export const messageQueries = {
  index: ({ organizationId, filters, page, pageSize }: MessageIndexScope = {}) =>
    indexTableQueryOptions({
      queryKey: messageQueryKeys.index({ organizationId, filters, page, pageSize }),
      queryFn: getMessages,
    }),
  show: ({ organizationId, messageId }: MessageScope) =>
    queryOptions({
      queryKey: messageQueryKeys.show({ organizationId, messageId }),
      queryFn: getMessage,
      placeholderData: () => {
        const cachedIndexQueries = queryClient.getQueriesData<MessageIndexResponse>({
          queryKey: messageQueryKeys.index({ organizationId }),
        })

        for (const [, response] of cachedIndexQueries) {
          const cachedMessage = response?.data.find((message) => message.id === Number(messageId))

          if (cachedMessage) {
            return cachedMessage
          }
        }

        return undefined
      },
    }),
}

function getMessageReadStateQueryKeys({ organizationId, messageId }: MessageReadStateScope) {
  return {
    showQueryKey: messageQueryKeys.show({ organizationId, messageId }),
    activityFeedQueryKey: activityFeedQueryKeys.all({ organizationId }),
    organizationQueryKey: organizationQueryKeys.show(organizationId),
  }
}

function captureMessageReadStateSnapshot(
  queryClient: QueryClient,
  scope: MessageReadStateScope
): MessageReadStateSnapshot {
  const { showQueryKey, activityFeedQueryKey, organizationQueryKey } =
    getMessageReadStateQueryKeys(scope)

  return {
    showQueryKey,
    activityFeedQueryKey,
    organizationQueryKey,
    previousShowMessage: queryClient.getQueryData<Message>(showQueryKey),
    previousActivityFeed: queryClient.getQueryData(activityFeedQueryKey),
    previousOrganization: queryClient.getQueryData<Organization>(organizationQueryKey),
  }
}

function restoreMessageReadState(
  queryClient: QueryClient,
  snapshot: MessageReadStateSnapshot | undefined
) {
  if (!snapshot) {
    return
  }

  queryClient.setQueryData(snapshot.showQueryKey, snapshot.previousShowMessage)
  queryClient.setQueryData(snapshot.activityFeedQueryKey, snapshot.previousActivityFeed)
  queryClient.setQueryData(snapshot.organizationQueryKey, snapshot.previousOrganization)
}

async function cancelMessageReadStateQueries(
  queryClient: QueryClient,
  scope: MessageReadStateScope
) {
  const { showQueryKey, activityFeedQueryKey, organizationQueryKey } =
    getMessageReadStateQueryKeys(scope)

  await Promise.all([
    queryClient.cancelQueries({ queryKey: showQueryKey }),
    queryClient.cancelQueries({ queryKey: activityFeedQueryKey }),
    queryClient.cancelQueries({ queryKey: organizationQueryKey }),
  ])
}

function updateMessageReadState(
  queryClient: QueryClient,
  { organizationId, messageId, readAt }: MessageReadStateScope & { readAt: string | null }
) {
  const showQueryKey = messageQueryKeys.show({ organizationId, messageId })
  const organizationQueryKey = organizationQueryKeys.show(organizationId)

  const previousMessage = queryClient.getQueryData<Message>(showQueryKey)
  const wasRead = Boolean(previousMessage?.read_at)
  const isRead = Boolean(readAt)

  queryClient.setQueryData<Message>(showQueryKey, (currentMessage) => {
    if (!currentMessage) {
      return currentMessage
    }

    return {
      ...currentMessage,
      read_at: readAt,
      is_read: isRead,
    }
  })

  if (wasRead === isRead) {
    return
  }

  queryClient.setQueryData<Organization>(organizationQueryKey, (currentOrganization) => {
    if (!currentOrganization || typeof currentOrganization.unread_messages_count !== 'number') {
      return currentOrganization
    }

    const unreadDelta = isRead ? -1 : 1

    return {
      ...currentOrganization,
      unread_messages_count: Math.max(0, currentOrganization.unread_messages_count + unreadDelta),
    }
  })
}

export function useMessages({
  organizationId,
  filters,
  page,
  pageSize,
  ...params
}: MessageIndexScope & QueryParams<ReturnType<typeof messageQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...messageQueries.index({
      organizationId: resolvedOrganizationId,
      filters,
      page,
      pageSize,
    }),
    ...params,
  })
}

export function useMessage(
  messageId: MessageIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof messageQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...messageQueries.show({
      organizationId: resolvedOrganizationId,
      messageId,
    }),
    ...params,
  })
}

export function useCreateMessageMutation() {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateMessageInput) => createMessage({ organizationId, ...values }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: messageQueryKeys.all })
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return queryClient.invalidateQueries({ queryKey: organizationQueryKeys.show(organizationId) })
    },
  })
}

export function useUpdateMessageMutation() {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateMessageInput & MessageMutationValues) =>
      updateMessage({ organizationId, ...values }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: messageQueryKeys.all })
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return queryClient.invalidateQueries({ queryKey: organizationQueryKeys.show(organizationId) })
    },
  })
}

export function useLinkMessageCustomerMutation() {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: MessageMutationValues & { customerId: CustomerIdInput | null }) =>
      linkMessageCustomer({ organizationId, ...values }),
    onSuccess: async (_response, { messageId }) => {
      await queryClient.invalidateQueries({
        queryKey: messageQueryKeys.show({ organizationId, messageId }),
      })
      await queryClient.invalidateQueries({ queryKey: messageQueryKeys.all })
      return queryClient.invalidateQueries({
        queryKey: customerQueryKeys.all({ organizationId }),
      })
    },
  })
}

export function useCreateMessageCustomerMutation() {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateMessageCustomerInput & MessageMutationValues) =>
      createMessageCustomer({ organizationId, ...values }),
    onSuccess: async (_response, { messageId }) => {
      await queryClient.invalidateQueries({
        queryKey: messageQueryKeys.show({ organizationId, messageId }),
      })
      await queryClient.invalidateQueries({ queryKey: messageQueryKeys.all })
      return queryClient.invalidateQueries({
        queryKey: customerQueryKeys.all({ organizationId }),
      })
    },
  })
}

export function useMarkMessageReadMutation({
  organizationId: scopedOrganizationId,
}: OrganizationScope = {}) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId(scopedOrganizationId)

  return useMutation({
    mutationFn: (messageId: MessageIdInput) => markMessageRead({ organizationId, messageId }),
    onMutate: async (messageId) => {
      await cancelMessageReadStateQueries(queryClient, {
        organizationId,
        messageId,
      })

      const snapshot = captureMessageReadStateSnapshot(queryClient, {
        organizationId,
        messageId,
      })

      updateMessageReadState(queryClient, {
        organizationId,
        messageId,
        readAt: new Date().toISOString(),
      })

      return snapshot
    },
    onError: (_error, _messageId, snapshot) => {
      restoreMessageReadState(queryClient, snapshot)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: messageQueryKeys.all })
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return queryClient.invalidateQueries({ queryKey: organizationQueryKeys.show(organizationId) })
    },
  })
}

export function useMarkMessageUnreadMutation({
  organizationId: scopedOrganizationId,
}: OrganizationScope = {}) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId(scopedOrganizationId)

  return useMutation({
    mutationFn: (messageId: MessageIdInput) => markMessageUnread({ organizationId, messageId }),
    onMutate: async (messageId) => {
      await cancelMessageReadStateQueries(queryClient, {
        organizationId,
        messageId,
      })

      const snapshot = captureMessageReadStateSnapshot(queryClient, {
        organizationId,
        messageId,
      })

      updateMessageReadState(queryClient, {
        organizationId,
        messageId,
        readAt: null,
      })

      return snapshot
    },
    onError: (_error, _messageId, snapshot) => {
      restoreMessageReadState(queryClient, snapshot)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: messageQueryKeys.all })
      await queryClient.invalidateQueries({
        queryKey: activityFeedQueryKeys.all({ organizationId }),
      })
      return queryClient.invalidateQueries({ queryKey: organizationQueryKeys.show(organizationId) })
    },
  })
}

const cleanupDeletedMessageQueries = ({
  queryClient,
  organizationId,
  messageId,
}: MessageReadStateScope & { queryClient: QueryClient }) => {
  queryClient.removeQueries({
    queryKey: messageQueryKeys.show({
      organizationId,
      messageId,
    }),
  })

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: messageQueryKeys.all }),
    queryClient.invalidateQueries({
      queryKey: activityFeedQueryKeys.all({ organizationId }),
    }),
    queryClient.invalidateQueries({ queryKey: organizationQueryKeys.show(organizationId) }),
  ])
}

export function useSoftDeleteMessageMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedMessage = (messageId: MessageIdInput) =>
    cleanupDeletedMessageQueries({ queryClient, organizationId, messageId })

  const mutation = useMutation({
    mutationFn: (messageId: MessageIdInput) => softDeleteMessage({ organizationId, messageId }),
    onSuccess: async (_, messageId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedMessage(messageId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedMessage,
  }
}
